// The ecology's plant windows on the client (server: simulation/src/ecowire.py has the format and the reasons, ecowin.py who is sent what; design: claude/ecology-artist-handoff.md).
//
// The ecology artist turns the painted ecology into individual plants; the server sends each viewer the plants round it in four size bands, small ones only near the eye, as "EPW" frames. A
// window is a block of land tiles (a twelfth of a cell, about 12 m) and holds the plants of one band in it. This file is the client's side of the wire:
//
//   * `isEcoFrame` tells an "EPW" frame from a physics frame and from the ground's "GCP", the cloud volume's "CVP" and the world painter's "WCP" frames;
//   * `EcoAssembler` puts the frames of a package together. A stream holds many windows and their packages follow one another, so unfinished packages are kept per package id (not one per
//     stream, as the ground rings do): a begin of the same window drops that window's older unfinished package, and a part whose package is no longer pending is ignored. When the package is whole the
//     ten planes are turned into per-form instance arrays (plants are sorted by form on the wire) and `onWindow` gets the window.
//
// Positions are in the client's frame: three x is tile x (east) and three z is minus tile y, as the ground windows. A window's plants are stored as offsets from the window's own base
// (x0, -y0) so the numbers stay small; the renderer adds the base back relative to the floating origin (origin.ts).
const HEAD = 12;
const KIND_BEGIN = 1, KIND_PART = 2;
const STREAM_BITS = 5;
const STREAM_MASK = (1 << STREAM_BITS) - 1;
const MSG_TYPE = 'eco_win';
const REC_BYTES = 10;
const MAX_PLANTS = 400_000;
const MAX_PENDING = 64;

/** The size bands. Must agree with ecowire.py's STREAMS (block in tiles; lo and hi the crown widths in metres; radius the distance within which the server sends a window; height the eye's limit). */
export interface EcoStreamSpec { block: number; lo: number; hi: number; radius: number; height: number }
export const ECO_STREAMS: readonly EcoStreamSpec[] = [
  { block: 4, lo: 0.1, hi: 0.5, radius: 20, height: 40 },
  { block: 12, lo: 0.5, hi: 2.0, radius: 100, height: 160 },
  { block: 24, lo: 2.0, hi: 6.0, radius: 300, height: 500 },
  { block: 48, lo: 6.0, hi: Infinity, radius: 1000, height: 1600 },
];
/** The meta version this client reads (ecowire.py META_VERSION). */
export const ECO_META_VERSION = 2;
/** A window is dropped here once its edge is this multiple of its stream's radius from the eye (the server keeps one out to 1.3). */
export const ECO_KEEP = 1.6;

/** The forms, as the artist numbers them. */
export const FORM_TREE = 0, FORM_SHRUB = 1, FORM_FERN = 2, FORM_GRASS = 3;

/** True for one of this module's frames ("EPW", version 1, ..., 0xFF in byte 7, which no physics frame's tick can have). */
export function isEcoFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x45 && b[1] === 0x50 && b[2] === 0x57 && b[3] === 1 && b[7] === 0xFF;
}

async function inflateZ(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface EcoPartSpec { o: number; n: number }
export interface EcoMeta {
  type: 'eco_win';
  v: number;
  stream: number;
  key: [number, number, number];
  n: number;
  /** tile position of the plants' quantised origin and the step of one count, metres */
  x0: number; y0: number; sx: number; sy: number;
  lo: number; hi: number | null;
  /** the block's south-west tile on the face (T0, U0) and its width in tiles */
  tiles: [number, number, number];
  /** the window's centre (tile metres) and half diagonal (m) */
  c: [number, number] | null; r: number;
  q: { h: [number, number]; c: [number, number]; d: [number, number]; l: [number, number] };
  bin: { len: { d: number }; parts: EcoPartSpec[] };
}

/** The plants of one form in a window, as the instance attributes the renderer wants (all Float32): loc (2 per plant: offset east and three z from the window's base), size (4: height, crown width,
 *  trunk or tuft width, leaf mass, all in m or kg), misc (2: facing in radians, the patch bin 0 to 7). */
export interface EcoFormPlants { count: number; loc: Float32Array; size: Float32Array; misc: Float32Array }
export interface EcoWindow {
  meta: EcoMeta;
  /** "stream/bi/bj" */
  id: string;
  /** three-frame position of the plants' origin: x0 and -y0 */
  baseX: number; baseZ: number;
  /** three-frame centre of the window (null when the server did not say) and its half diagonal */
  cx: number; cz: number; r: number;
  forms: [EcoFormPlants, EcoFormPlants, EcoFormPlants, EcoFormPlants];
  total: number;
}

const gcd = (a: number, b: number): number => { while (b) { [a, b] = [b, a % b]; } return a; };
/** a step coprime with n, about 0.618 n (so a walk of k * step mod n visits every slot once and spreads evenly) */
export function strideFor(n: number): number {
  if (n < 3) return 1;
  let p = Math.max(1, Math.round(n * 0.6180339887));
  while (gcd(p, n) !== 1) p++;
  return p;
}

const dlog = (q: number, lo: number, hi: number): number => (q > 0 ? lo * Math.exp(((q - 1) / 254) * Math.log(hi / lo)) : 0);

/** The ten planes of a whole package into the window's per-form instance arrays. */
export function buildWindow(meta: EcoMeta, d: Uint8Array): EcoWindow {
  const n = meta.n;
  const xl = d.subarray(0, n), xh = d.subarray(n, 2 * n), yl = d.subarray(2 * n, 3 * n), yh = d.subarray(3 * n, 4 * n);
  const hq = d.subarray(4 * n, 5 * n), cq = d.subarray(5 * n, 6 * n), dq = d.subarray(6 * n, 7 * n), lq = d.subarray(7 * n, 8 * n), yw = d.subarray(8 * n, 9 * n), fl = d.subarray(9 * n, 10 * n);
  const counts = [0, 0, 0, 0];
  for (let i = 0; i < n; i++) counts[fl[i]! & 3]!++;
  const forms = counts.map(c => ({ count: c, loc: new Float32Array(2 * c), size: new Float32Array(4 * c), misc: new Float32Array(2 * c) })) as EcoWindow['forms'];
  // The plants of a form are sorted by tile on the wire, so the first share of them is a patch of ground, not a thinner forest: a fixed stride through the arrays spreads them (the renderer's
  // quality setting draws the first share of the instances, which is then an even thinning).
  const stride = counts.map(strideFor);
  const at = [0, 0, 0, 0];
  // lookup tables: the log quantisers have 256 codes
  const th = new Float32Array(256), tc = new Float32Array(256), td = new Float32Array(256), tl = new Float32Array(256);
  for (let q = 0; q < 256; q++) { th[q] = dlog(q, meta.q.h[0], meta.q.h[1]); tc[q] = dlog(q, meta.q.c[0], meta.q.c[1]); td[q] = dlog(q, meta.q.d[0], meta.q.d[1]); tl[q] = dlog(q, meta.q.l[0], meta.q.l[1]); }
  for (let i = 0; i < n; i++) {
    const f = fl[i]! & 3, F = forms[f]!, k = (at[f]!++ * stride[f]!) % F.count;
    F.loc[2 * k] = meta.sx * (xl[i]! | (xh[i]! << 8));
    F.loc[2 * k + 1] = -meta.sy * (yl[i]! | (yh[i]! << 8));
    F.size[4 * k] = th[hq[i]!]!; F.size[4 * k + 1] = tc[cq[i]!]!; F.size[4 * k + 2] = td[dq[i]!]!; F.size[4 * k + 3] = tl[lq[i]!]!;
    F.misc[2 * k] = (yw[i]! / 256) * Math.PI * 2;
    F.misc[2 * k + 1] = (fl[i]! >> 2) & 7;
  }
  const c = meta.c;
  return {
    meta, id: meta.key.join('/'), baseX: meta.x0, baseZ: -meta.y0,
    cx: c ? c[0] : NaN, cz: c ? -c[1] : NaN, r: meta.r, forms, total: n,
  };
}

interface Assembly { id: number; meta: EcoMeta; data: Uint8Array; left: number }

/** Collects the frames of the packages in flight (see the file's head). `onWindow` gets each whole window. */
export class EcoAssembler {
  private pending = new Map<number, Assembly>();
  /** packages abandoned unfinished (a newer one of the same window began first), for the console */
  dropped = 0;

  constructor(private readonly onWindow: (w: EcoWindow) => void, private readonly onError?: (e: unknown) => void) {}

  accept(buf: ArrayBuffer): void {
    const v = new DataView(buf);
    const kind = v.getUint8(4), id = v.getUint32(8, true);
    if (kind === KIND_BEGIN) {
      let meta: EcoMeta;
      try {
        meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 16, v.getUint32(12, true)))) as EcoMeta;
        if (meta.type !== MSG_TYPE || !meta.bin || !Array.isArray(meta.bin.parts) || !Array.isArray(meta.key)) throw new Error('not an ecology window');
        if (meta.v !== ECO_META_VERSION) throw new Error(`ecology window of version ${meta.v}: this client reads version ${ECO_META_VERSION} (the server and the client are not from the same build)`);
        if (!(meta.n >= 0 && meta.n <= MAX_PLANTS) || meta.bin.len.d !== meta.n * REC_BYTES || meta.stream !== (id & STREAM_MASK) || meta.stream >= ECO_STREAMS.length
          || meta.bin.parts.length !== (meta.n === 0 ? 0 : REC_BYTES)) throw new Error('ecology window: the meta does not describe a window');
      } catch (e) { this.onError?.(e); return; }
      const key = meta.key.join('/');
      for (const [pid, a] of this.pending) if (a.meta.key.join('/') === key) { this.pending.delete(pid); this.dropped++; }
      while (this.pending.size >= MAX_PENDING) { this.pending.delete(this.pending.keys().next().value as number); this.dropped++; }
      const a: Assembly = { id, meta, data: new Uint8Array(meta.bin.len.d), left: meta.bin.parts.length };
      if (a.left === 0) { this.onWindow(buildWindow(meta, a.data)); return; }
      this.pending.set(id, a);
      return;
    }
    const a = this.pending.get(id);
    if (kind !== KIND_PART || !a) return;
    const spec = a.meta.bin.parts[v.getUint16(12, true)];
    if (!spec) return;
    inflateZ(new Uint8Array(buf, 14)).then(raw => {
      if (this.pending.get(id) !== a) return;                                  // the window was superseded while this part was inflating
      if (raw.length !== spec.n || spec.o + spec.n > a.data.length) throw new Error(`ecology window part @${spec.o}: ${raw.length} bytes, expected ${spec.n}`);
      a.data.set(raw, spec.o);
      if (--a.left === 0) { this.pending.delete(id); this.onWindow(buildWindow(a.meta, a.data)); }
    }).catch(e => { if (this.pending.get(id) === a) this.pending.delete(id); this.onError?.(e); });
  }
}

/** The windows the client holds, by id. The newest package of a window replaces the old one; windows far from the eye are dropped. */
export class EcoStore {
  readonly windows = new Map<string, EcoWindow>();
  /** bumped whenever the set of windows changes (the view redoes its meshes) */
  version = 0;
  /** windows taken since construction (a counter for the overlay and the tests) */
  taken = 0;

  accept(w: EcoWindow): void {
    this.windows.set(w.id, w);
    this.version++;
    this.taken++;
  }

  /** Forget the windows whose edge is further than ECO_KEEP times their stream's radius from the eye (three x, z). Returns how many went. */
  prune(x: number, z: number): number {
    let gone = 0;
    for (const [id, w] of this.windows) {
      if (!Number.isFinite(w.cx)) continue;
      const s = ECO_STREAMS[w.meta.stream]!;
      if (Math.hypot(w.cx - x, w.cz - z) - w.r > s.radius * ECO_KEEP) { this.windows.delete(id); gone++; }
    }
    if (gone) this.version++;
    return gone;
  }

  clear(): void { if (this.windows.size) { this.windows.clear(); this.version++; } }

  get plants(): number { let n = 0; for (const w of this.windows.values()) n += w.total; return n; }
}
